// `GET /api/line-items/{id}/type-suggestions?scope=personal|business` (D-D).
// Real, server-computed recommendations for the TypeChooser popover's
// "Suggested" section -- never fabricated names. `reason` is carried on the
// wire for now (backend/schemas/line_items.py::TypeSuggestion) but is
// internal only (AGENTS.md Data & UI honesty 4): the UI groups suggestions
// under the plain header "Suggested" and must never render `reason` text.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

// ENG (deferrable): the backend types `reason` as a plain `str`
// (`backend/schemas/line_items.py::TypeSuggestion`), not a closed set -- a
// `z.enum` here was STRICTER than the actual contract, so a new backend tier
// would fail `parseEnvelope` and kill the whole "Suggested" section for a
// field the UI never renders anyway (AGENTS.md Data & UI honesty 4: it's
// internal-only, carried on the wire but never displayed).
export const typeSuggestionReasonSchema = z.string();

export const typeSuggestionSchema = z.object({
  category_id: z.string(),
  slug: z.string(),
  name: z.string(),
  reason: typeSuggestionReasonSchema,
});
export type TypeSuggestion = z.infer<typeof typeSuggestionSchema>;

export const typeSuggestionsResponseSchema = z.object({
  suggestions: z.array(typeSuggestionSchema),
  scope: z.enum(['personal', 'business', 'transfer', 'any']),
});
export type TypeSuggestionsResponse = z.infer<typeof typeSuggestionsResponseSchema>;

/**
 * `scope` defaults server-side to the item's current scope; pass it
 * explicitly for an uncategorized item (the backend 422s otherwise -- no
 * invented default). `'transfer'`/`'any'` back the TypeChooser's
 * scope-agnostic mode (DES-R1-001/002). `enabled` lets the caller defer the
 * request until the popover actually opens.
 */
export function useTypeSuggestions(
  lineItemId: string | null | undefined,
  options?: { scope?: 'personal' | 'business' | 'transfer' | 'any'; enabled?: boolean },
) {
  const scope = options?.scope;
  return useQuery({
    queryKey: ['line-items', lineItemId, 'type-suggestions', scope ?? null],
    queryFn: async () => {
      const response = await client.get(`/api/line-items/${lineItemId}/type-suggestions`, {
        params: scope ? { scope } : undefined,
      });
      return parseEnvelope(typeSuggestionsResponseSchema, response.data).data;
    },
    enabled: (options?.enabled ?? true) && !!lineItemId,
  });
}
